import React from 'react';
import {
	Box,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useFormikContext } from 'formik';
import { parseToInsightDateString, safelyParseDate } from '@/utils/date';
import PrimaryContactCheckbox from '../PrimaryContactCheckbox';
import {
	RELATIONSHIP_CONTACT_STATUS_OPTIONS,
	RELATIONSHIP_CONTACT_TYPE_OPTIONS,
} from '@/utils/relationshipContactOptions';
import { RelationshipCompanySelect } from '@/components/selects/RelationshipCompanySelect';
import { RelationshipContactSelect } from '@/components/selects/RelationshipContactSelect';
import FolioCheckbox from '@/components/FolioCheckbox';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import {
	SEARCH_RELATIONSHIP_COMPANY_HELP_TEXT,
	SEARCH_RELATIONSHIP_CONTACT_HELP_TEXT,
} from '@/utils/helperText';

const CONTACT_DETAIL_FIELDS = [
	'firstName',
	'lastName',
	'email',
	'mobile',
	'roleTitle',
	'notesSummary',
	'nextActionDescription',
	'lastContactedAt',
	'nextActionDate',
];

const RelationshipContactFormFields = ({
	disabled = false,
	relationshipContactId,
	allowExistingIndividual = false,
}) => {
	const {
		values,
		errors,
		touched,
		getFieldProps,
		setFieldValue,
		setFieldTouched,
	} = useFormikContext();
	const { relationshipCompany } = useObjectContext();
	const { getFeatureFlags } = useFeatureFlagContext();
	const isRelationshipContactsEnabled = Boolean(getFeatureFlags('relationshipContacts')?.active);
	const displayCompanySearch =
		isRelationshipContactsEnabled && !relationshipCompany && !relationshipContactId;
	const isExistingIndividual = Boolean(allowExistingIndividual && values.isExistingRelationshipContact);

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Grid
				container
				spacing={2}
				sx={{
					width: '100%',
					maxWidth: '100%',
					minWidth: 0,
				}}
			>
				{allowExistingIndividual && (
					<Grid size={12}>
						<FolioCheckbox
							checked={Boolean(values.isExistingRelationshipContact)}
							label='Select existing contact'
							disabled={disabled}
							onChange={(event) => {
								const checked = event.target.checked;
								setFieldValue('isExistingRelationshipContact', checked);
								if (checked) {
									CONTACT_DETAIL_FIELDS.forEach((field) => setFieldValue(field, ''));
								} else {
									setFieldValue('existingRelationshipContact', null);
								}
							}}
						/>
					</Grid>
				)}
				{isExistingIndividual ? (
					<Grid size={12}>
						<FormLabel required>Search contact</FormLabel>
						<RelationshipContactSelect
							disabled={disabled}
							required
							error={Boolean(
								touched.existingRelationshipContact && errors.existingRelationshipContact,
							)}
							value={values.existingRelationshipContact}
							onChange={(individual) => {
								setFieldValue('existingRelationshipContact', individual);
								setFieldTouched('existingRelationshipContact', true, false);
							}}
						/>
						{touched.existingRelationshipContact && errors.existingRelationshipContact && (
							<FormHelperText error>{errors.existingRelationshipContact}</FormHelperText>
						)}
						<FormHelperText>{SEARCH_RELATIONSHIP_CONTACT_HELP_TEXT}</FormHelperText>
					</Grid>
				) : (
					<>
				<Grid size={{ xs: 12, sm: 6 }}>
					<FormControl
						fullWidth
						required
						disabled={disabled}
						error={Boolean(touched.relationshipType && errors.relationshipType)}
					>
						<FormLabel required>Type</FormLabel>

						<Select
							id='relationshipType'
							displayEmpty
							fullWidth
							disabled={disabled}
							error={Boolean(
								touched.relationshipType && errors.relationshipType,
							)}
							{...getFieldProps('relationshipType')}
						>
							{RELATIONSHIP_CONTACT_TYPE_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>

						{touched.relationshipType && errors.relationshipType && (
							<FormHelperText error>
								{errors.relationshipType}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormControl
						fullWidth
						required
						disabled={disabled}
						error={Boolean(touched.relationshipStatus && errors.relationshipStatus)}
					>
						<FormLabel required>Status</FormLabel>

						<Select
							id='relationshipStatus'
							displayEmpty
							fullWidth
							disabled={disabled}
							error={Boolean(touched.relationshipStatus && errors.relationshipStatus)}
							{...getFieldProps('relationshipStatus')}
						>
							{RELATIONSHIP_CONTACT_STATUS_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>

						{touched.relationshipStatus && errors.relationshipStatus && (
							<FormHelperText error>
								{errors.relationshipStatus}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel required>First name</FormLabel>

					<TextField
						id='firstName'
						fullWidth
						disabled={disabled}
						error={Boolean(touched.firstName && errors.firstName)}
						{...getFieldProps('firstName')}
					/>

					{touched.firstName && errors.firstName && (
						<FormHelperText error>
							{errors.firstName}
						</FormHelperText>
					)}
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel>Last name</FormLabel>

					<TextField
						id='lastName'
						fullWidth
						disabled={disabled}
						error={Boolean(touched.lastName && errors.lastName)}
						{...getFieldProps('lastName')}
					/>

					{touched.lastName && errors.lastName && (
						<FormHelperText error>
							{errors.lastName}
						</FormHelperText>
					)}
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel required>Email</FormLabel>

					<TextField
						id='email'
						fullWidth
						disabled={disabled}
						error={Boolean(touched.email && errors.email)}
						{...getFieldProps('email')}
					/>

					{touched.email && errors.email && (
						<FormHelperText error>{errors.email}</FormHelperText>
					)}
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel>Role</FormLabel>

					<TextField
						id='roleTitle'
						fullWidth
						disabled={disabled}
						error={Boolean(touched.roleTitle && errors.roleTitle)}
						{...getFieldProps('roleTitle')}
					/>

					{touched.roleTitle && errors.roleTitle && (
						<FormHelperText error>
							{errors.roleTitle}
						</FormHelperText>
					)}
				</Grid>
					</>
				)}

				{displayCompanySearch && (
					<Grid size={12}>
						<FormLabel>Search company</FormLabel>
						<RelationshipCompanySelect
							disabled={disabled}
							error={Boolean(touched.relationshipCompanyName && errors.relationshipCompanyName)}
							value={
								values.relationshipCompanyId
									? {
											id: values.relationshipCompanyId,
											name: values.relationshipCompanyName,
										}
									: values.relationshipCompanyName
							}
							onChange={({ relationshipCompanyId, relationshipCompanyName }) => {
								setFieldValue('relationshipCompanyId', relationshipCompanyId ?? '');
								setFieldValue('relationshipCompanyName', relationshipCompanyName ?? '');
								setFieldValue('isPrimary', false);
								setFieldTouched('relationshipCompanyName', true, true);
							}}
						/>
						{((touched.relationshipCompanyName && errors.relationshipCompanyName) ||
							(touched.relationshipCompanyId && errors.relationshipCompanyId)) && (
							<FormHelperText error>
								{errors.relationshipCompanyName || errors.relationshipCompanyId}
							</FormHelperText>
						)}
						<FormHelperText>{SEARCH_RELATIONSHIP_COMPANY_HELP_TEXT}</FormHelperText>
					</Grid>
				)}
			{values.relationshipCompanyId && (
				<Grid size={{ xs: 12, sm: 6 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							height: '100%',
							pt: 3,
						}}
					>
						<PrimaryContactCheckbox
							disabled={disabled}
							relationshipContactId={relationshipContactId}
						/>
					</Box>
				</Grid>
			)}
				{!isExistingIndividual && (
					<>
				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel>Mobile</FormLabel>
					<TextField
						id='mobile'
						fullWidth
						disabled={disabled}
						error={Boolean(touched.mobile && errors.mobile)}
						{...getFieldProps('mobile')}
					/>

					{touched.mobile && errors.mobile && (
						<FormHelperText error>{errors.mobile}</FormHelperText>
					)}
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel>Last contacted</FormLabel>

					<DatePicker
						disabled={disabled}
						format='dd/MM/yyyy'
						sx={{ width: '100%' }}
						slotProps={{
							textField: {
								id: 'lastContactedAt',
								fullWidth: true,
								error: Boolean(touched.lastContactedAt && errors.lastContactedAt),
								onBlur: () => setFieldTouched('lastContactedAt', true),
							},
						}}
						value={safelyParseDate(values.lastContactedAt)}
						onChange={(newValue) =>
							setFieldValue(
								'lastContactedAt',
								parseToInsightDateString(newValue) ?? '',
							)
						}
					/>

					{touched.lastContactedAt && errors.lastContactedAt && (
						<FormHelperText error>
							{errors.lastContactedAt}
						</FormHelperText>
					)}
				</Grid>

				<Grid size={{ xs: 12, sm: 6 }}>
					<FormLabel>Next action date</FormLabel>

					<DatePicker
						disabled={disabled}
						format='dd/MM/yyyy'
						sx={{ width: '100%' }}
						slotProps={{
							textField: {
								id: 'nextActionDate',
								fullWidth: true,
								error: Boolean(touched.nextActionDate && errors.nextActionDate),
								onBlur: () => setFieldTouched('nextActionDate', true),
							},
						}}
						value={safelyParseDate(values.nextActionDate)}
						onChange={(newValue) =>
							setFieldValue(
								'nextActionDate',
								parseToInsightDateString(newValue) ?? '',
							)
						}
					/>

					{touched.nextActionDate && errors.nextActionDate && (
						<FormHelperText error>
							{errors.nextActionDate}
						</FormHelperText>
					)}
				</Grid>

				<Grid size={12}>
					<FormLabel>Next action description</FormLabel>

					<TextField
						id='nextActionDescription'
						fullWidth
						disabled={disabled}
						error={Boolean(
							touched.nextActionDescription &&
								errors.nextActionDescription,
						)}
						{...getFieldProps('nextActionDescription')}
					/>

					{touched.nextActionDescription &&
						errors.nextActionDescription && (
							<FormHelperText error>
								{errors.nextActionDescription}
							</FormHelperText>
						)}
				</Grid>

				<Grid size={12}>
					<FormLabel>Notes</FormLabel>

					<TextField
						id='notesSummary'
						fullWidth
						multiline
						minRows={3}
						disabled={disabled}
						error={Boolean(touched.notesSummary && errors.notesSummary)}
						{...getFieldProps('notesSummary')}
					/>

					{touched.notesSummary && errors.notesSummary && (
						<FormHelperText error>
							{errors.notesSummary}
						</FormHelperText>
					)}
				</Grid>
					</>
				)}
			</Grid>
		</LocalizationProvider>
	);
};

export default RelationshipContactFormFields;
